import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/store/auth'
import { Button, Card } from '@diwata/ironvale'
import { CapabilitySplit } from '@/components/CapabilitySplit'

export function CovenantPage() {
  const { id } = useParams<{ id: string }>()
  const api = useAuth((s) => s.api)()
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [role, setRole] = useState('')
  const [boundaries, setBoundaries] = useState('')
  const [saving, setSaving] = useState(false)

  const { data: covenant, isLoading } = useQuery({
    queryKey: ['covenant', id],
    queryFn: () => api.getCovenant(id!),
    enabled: !!id,
  })

  function startEdit() {
    setRole(covenant?.role ?? '')
    setBoundaries((covenant?.boundaries ?? []).join('\n'))
    setEditing(true)
  }

  async function save() {
    setSaving(true)
    try {
      await api.updateCovenant(id!, {
        role,
        boundaries: boundaries.split('\n').map((s) => s.trim()).filter(Boolean),
      })
      setEditing(false)
      qc.invalidateQueries({ queryKey: ['covenant', id] })
    } finally { setSaving(false) }
  }

  if (isLoading) return <p className="cp-empty">Loading…</p>
  if (!covenant) return <p className="cp-empty">No Covenant found.</p>

  return (
    <div style={{ maxWidth: 560 }}>
      <div className="cp-page-header" style={{ marginBottom: 20 }}>
        <div style={{ fontWeight: 600 }}>
          Covenant <span style={{ fontSize: 12, color: 'var(--ae-color-text-muted)', fontWeight: 400 }}>v{covenant.version}</span>
        </div>
        {!editing && <Button variant="ghost" size="sm" onClick={startEdit}>Edit</Button>}
      </div>

      {editing ? (
        <div className="cp-form">
          <div className="cp-field">
            <label className="cp-label">Role</label>
            <textarea className="cp-textarea" value={role} onChange={(e) => setRole(e.target.value)} style={{ minHeight: 120 }} />
          </div>
          <div className="cp-field">
            <label className="cp-label">Boundaries <span style={{ fontWeight: 400, color: 'var(--ae-color-text-muted)' }}>(one per line)</span></label>
            <textarea className="cp-textarea" value={boundaries} onChange={(e) => setBoundaries(e.target.value)} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
            <Button size="sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <CapabilitySplit tools={covenant.tools} />
          <Card>
            <div className="cp-label" style={{ marginBottom: 8 }}>Role</div>
            <div style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>
              {covenant.role || <span className="cp-empty">Not set</span>}
            </div>
          </Card>
          {covenant.boundaries.length > 0 && (
            <Card>
              <div className="cp-label" style={{ marginBottom: 8 }}>Boundaries</div>
              <ul style={{ paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {covenant.boundaries.map((b, i) => <li key={i} style={{ fontSize: 14 }}>{b}</li>)}
              </ul>
            </Card>
          )}
        </div>
      )}
    </div>
  )
}
